import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  ListPageSkeleton,
  RefreshingIndicator,
} from "@shared/components/ListLoading";
import { useAppSelector } from "@redux/hooks";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import {
  CarrierBridge,
  carrierBridgeColumns as baseColumns,
  carrierBridgeIdHeader,
  shapeCarrierBridgeData,
} from "./CarrierBridgeData";
import { makeCarrierBridgeFields } from "./carrierBridgeFieldSpec";
import { fetchCarrierBridgeList } from "./requests/request";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";


/**
 * Services → L2 → Carrier Bridge (CTX-725): the tenant's Carrier Bridge
 * services, and every customer's for a partner viewing all customers.
 */
const MainCarrierBridge: React.FC = () => {
  const api = useAxiosAuth0();
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId != null && tenantId === partnerId;

  const [list, setList] = useState<CarrierBridge[]>([]);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  const loadList = useCallback(async () => {
    if (tenantId == null) return;
    setIsFetchingList(true);
    setFetchError(null);
    try {
      const { status, data } = await fetchCarrierBridgeList(api, tenantId);
      if (status >= 500 || data.success === false) {
        setFetchError(data.message || "Unknown server error");
        setList([]);
        return;
      }
      setList(Array.isArray(data.data) ? shapeCarrierBridgeData(data.data) : []);
    } catch (err) {
      console.error("Error fetching Carrier Bridge list", err);
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? err.message
          : "Network error";
      setFetchError(message);
      setList([]);
    } finally {
      setIsFetchingList(false);
      setHasFetched(true);
    }
  }, [api, tenantId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const fields = useMemo(
    () => makeCarrierBridgeFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );
  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const normalized = useMemo(
    () =>
      normalizeFilters<CarrierBridge>(activeFilters, fields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, fields]
  );
  const predicate = useMemo(
    () => buildPredicate<CarrierBridge>(normalized, fields),
    [normalized, fields]
  );
  const filteredData = useMemo(() => list.filter(predicate), [list, predicate]);

  // A partner viewing all customers opens the detail as the row's owner. The
  // tenant rides in the URL, not in history state, so a reload, a pasted link
  // or a view restored later reads the same scope (CTX-861 / CTX-773).
  const detailPath = useCallback(
    (row: CarrierBridge) =>
      buildDetailPath(
        "carrierBridge",
        { carrierBridgeId: row.id },
        { tenantId: isViewingAllCustomers ? row.tenantId : tenantId }
      ),
    [isViewingAllCustomers, tenantId]
  );

  const columns = useMemo<GenericTableColumn<CarrierBridge>[]>(() => {
    const cols = baseColumns.map((col) =>
      col.header === carrierBridgeIdHeader
        ? {
            ...col,
            renderCell: (row: CarrierBridge) => (
              <button
                type="button"
                {...viewHref(detailPath(row))}
                onClick={() => navigate(detailPath(row))}
                className="font-medium underline text-semantic-text-brand hover:underline focus:underline"
              >
                {row.serviceId}
              </button>
            ),
          }
        : col
    );
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        id: "customer",
        sortAccessor: (row) => row.customer?.name ?? "",
        renderCell: (row) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-semantic-text-secondary">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    return cols;
  }, [isViewingAllCustomers, detailPath, navigate]);

  if (!hasFetched) return <ListPageSkeleton columns={columns.length} />;
  if (fetchError) return <ErrorFetchingItems text={fetchError} />;
  if (list.length === 0) return <EmptyItems />;

  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <RefreshingIndicator active={isFetchingList} />
      <GenericCard
        isCollapsible={false}
        className="h-full flex flex-col"
        bodyContent={
          <div className="flex flex-col h-full min-h-0">
            <div className="flex items-center justify-between gap-3 my-5">
              <div className="w-full max-w-full sm:max-w-[500px]">
                <GenericSearchbar
                  placeholder="Find by attribute or value"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering
                  debounceMs={200}
                />
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <GenericTable
                columns={columns}
                data={filteredData}
                initialRowsPerPage={10}
                rowsPerPageOptions={[10, 20, 30]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight="100%"
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default MainCarrierBridge;
